import {Link as RouterLink} from 'react-router-dom';
import {Chip, Link} from '@mui/material';
import {MRT_ColumnDef, MRT_TableOptions} from 'material-react-table';

import {UserBan, UserHoverCard} from '@entities/user';
import {MrTable} from '@shared/components';
import {
  DATETIME_FORMAT,
  entityTableCommonProps,
  ROUTE__USER_ACCOUNT,
} from '@shared/constants';
import {formatInTz} from '@shared/utils';

type UserBanTableItem = UserBan & {username?: string};

const columns: MRT_ColumnDef<UserBanTableItem>[] = [
  {
    accessorKey: 'comment',
    header: 'Comment',
    size: 300,
    Cell: ({cell}) => (
      <span style={{whiteSpace: 'pre-wrap', overflowWrap: 'anywhere'}}>
        {cell.getValue<string>()}
      </span>
    ),
  },
  {
    id: 'status',
    header: 'Status',
    Cell: ({row}) => (
      <Chip
        size="small"
        label={row.original.liftedAt ? 'Lifted' : 'Active'}
        color={row.original.liftedAt ? 'default' : 'error'}
      />
    ),
  },
  {
    accessorKey: 'bannedAt',
    header: 'Banned at',
    Cell: ({row}) => formatInTz(row.original.bannedAt, DATETIME_FORMAT),
    size: 180,
  },
  {
    accessorKey: 'bannedByUsername',
    header: 'Banned by',
    Cell: ({row}) => (
      <UserHoverCard userId={row.original.bannedBy}>
        <Link
          component={RouterLink}
          to={ROUTE__USER_ACCOUNT.replace(':id', row.original.bannedBy)}
        >
          {row.original.bannedByUsername}
        </Link>
      </UserHoverCard>
    ),
  },
  {
    accessorKey: 'liftedAt',
    header: 'Lifted at',
    Cell: ({row}) =>
      row.original.liftedAt
        ? formatInTz(row.original.liftedAt, DATETIME_FORMAT)
        : '\u2014',
    size: 180,
  },
  {
    accessorKey: 'liftedByUsername',
    header: 'Lifted by',
    Cell: ({row}) =>
      row.original.liftedBy ? (
        <UserHoverCard userId={row.original.liftedBy}>
          <Link
            component={RouterLink}
            to={ROUTE__USER_ACCOUNT.replace(':id', row.original.liftedBy)}
          >
            {row.original.liftedByUsername ?? row.original.liftedBy}
          </Link>
        </UserHoverCard>
      ) : (
        '\u2014'
      ),
  },
];

interface Props extends Omit<MRT_TableOptions<UserBanTableItem>, 'columns'> {
  error?: Error | null;
  localStorageKeyForSettings?: string;
  showUser?: boolean;
}

export const UserBansTable = ({showUser = false, ...props}: Props) => (
  <MrTable
    {...entityTableCommonProps}
    enableRowActions={false}
    enableSorting={false}
    columns={
      showUser
        ? [
            {
              id: 'user',
              header: 'User',
              size: 180,
              Cell: ({row}) => (
                <UserHoverCard userId={row.original.userId}>
                  <Link
                    component={RouterLink}
                    to={ROUTE__USER_ACCOUNT.replace(':id', row.original.userId)}
                  >
                    {row.original.username ?? row.original.userId}
                  </Link>
                </UserHoverCard>
              ),
            },
            ...columns,
          ]
        : columns
    }
    {...props}
  />
);
